import React, { useEffect, useState } from 'react';
import './PartialRenewalForm.scss';
import {
    formatProductTerm,
    formatCurrency,
    formatTime,
    formatDate,
    formatDestinationAccountType
} from "../../../../utils/helpers";
import CurrencyInput from "../../../Form/TextInput/CurrencyInput";
import InfoMessage from "./InfoMessage/InfoMessage";
import TransferSection from "./TransferSection/TransferSection";

import Select from "react-select/base";
import {MaturityTerm} from "../../MaturityTerm";
import WarningMessage from "./WarningMessage/WarningMessage";
import {constructDateTime} from "../../MaturityFlowHelperFunctions";

/** @import {PartialRenewalFormProps} from './PartialRenewalFormTypes' */
/** @import {Account, Product} from '../../../../contexts/CustomerDataProviderTypes' */

/** @import {OtherAccount} from './TransferSection/TransferSectionTypes' */
export function PartialRenewalForm(/** @type {PartialRenewalFormProps} */ {
    maturingAccount,
    products,
    accountList,
    currencyInputValue,
    setCurrencyInputValue,
    selectedAccount,
    setSelectedAccount, selectedProduct, setSelectedProduct}) 
{
    const [transferInputValue, setTransferInputValue] = useState(0);
    const [currencyInputError, setCurrencyInputError] = useState("");

    const handleCurrencyInputChange = (/** @type {string} */value) => {
        setCurrencyInputValue(value);

        const numericValue = Number(String(value).replace(/,/g, ''));
        let errorMessage = "";

        if (numericValue > Number(maturingAccount?.balance)) {
            errorMessage = "You have up to " + formatCurrency(maturingAccount?.balance) + " available to reinvest – you can top up later";
        } else if (numericValue > 250000) {
            errorMessage = "You cannot reinvest more than £250,000.00";
        }
        else if (numericValue < 5000) {
            errorMessage = "You cannot reinvest an amount less than £5,000.00";
        }

        setCurrencyInputError(errorMessage);
        if (numericValue >= Number(maturingAccount?.balance) || numericValue <= 5000)
        {
            setSelectedAccount(undefined);
        }
    };
    
    useEffect(() => {
        const calculatedTransferValue = Number(maturingAccount?.balance) - Number(String(currencyInputValue).replace(/,/g, ''));
        setTransferInputValue(calculatedTransferValue > 0 ? calculatedTransferValue : 0);

    }, [currencyInputValue, maturingAccount?.balance, selectedAccount]);

    const formatCurrencyInputValue = (/** @type {string | undefined |null} */ value) =>
        (typeof value === 'number' && !isNaN(value)) ? Number(value).toFixed(2) : value;
    const getCalculatedInterest = (/** @type {Account} */ maturingAccount) => {
        if (!maturingAccount.maturity?.calculatedTotalInterest && maturingAccount.maturity?.calculatedBalanceAtMaturity) {
            return maturingAccount.maturity.calculatedBalanceAtMaturity - maturingAccount.balance;
        }

        return maturingAccount.maturity?.calculatedTotalInterest;
    }
    const renderWarningMessage = () => {
        if (!selectedAccount) {
            return null;
        }

        const accountType = selectedAccount.product.name;
        let bodyMessage;

        if (accountType === "Linked Account") {
            bodyMessage = `Please make sure your linked account is still active, and that we have the right
                       details. If you need to update it, go to <a href="/account-details">Manage account(s)</a> 
                       to update your details before you give us any instructions.`;
        } else {
            bodyMessage = `Please note: if your ${formatDestinationAccountType(selectedAccount.product.type)} is closed
                       on ${maturingAccount?.maturity ? formatDate(maturingAccount.maturity.maturityDate) : ''},
                       or if this payment would take the balance over ${formatCurrency(selectedAccount.product.maxWithdrawalLimit, false)},
                       we’ll pay the whole amount into your linked account instead.`;
        }

        return (
            <WarningMessage
                body={bodyMessage}
                selectedAccountType={accountType}
            />
        );
    };

    return (
        <>
            <InfoMessage
                title={"Interest earned: " + formatCurrency((maturingAccount ? getCalculatedInterest(maturingAccount) : 0) || 0)}
                body={`Your interest will be paid into your linked account, no matter where you have some of your balance paid. 
                If you'd also like to reinvest your interest (up to the £250,000 maximum balance), 
                choose "renew everything" instead.`}
            />
            <main className={"renewalForm"}>
                <section className={"formContainer"}>
                    <div className={"formWrapper"}>
                        <div className={"formContent"}>
                            <div className={"formColumns"}>
                                <div className={"leftColumn"}>
                                    <span className={"section_Title"}>Renewal amount</span>
                                    <div className={"transferAmount"}>
                                        <div className={"text-input__wrapper"}>
                                            <label className={"text-input__label"}>How much would you like to reinvest?</label>
                                            <CurrencyInput
                                                placeholder="0.00"
                                                value={formatCurrencyInputValue(currencyInputValue)?.toString() || ''}
                                                onChange={handleCurrencyInputChange}
                                                hasError={!!currencyInputError}
                                                className={`currency__input ${currencyInputError ? 'error' : ''}`}
                                            />
                                            {currencyInputError ? (
                                                <div className="errorContainer">
                                                    <span className="error-message">{currencyInputError}</span>
                                                </div>
                                            ) : null}  </div>
                                    </div>
                                    <div>
                                        <div className={"text-input__wrapper"}>
                                            <label className={"text-input__label custom-label"}>How long would you like
                                                to reinvest for?</label>
                                            <MaturityTerm
                                                maturingAccount={maturingAccount}
                                                products={products}
                                                selectedProduct={selectedProduct}
                                                setSelectedProduct={setSelectedProduct}
                                                applyMaxWidth={false}
                                                //isDisabled={isMaturityTermDisabled}
                                            />
                                        </div>
                                    </div>
                                </div>
                                <div className={"divider"}/>
                                <TransferSection
                                    maturingAccount={maturingAccount}
                                    accountList={accountList}
                                    transferInputValue={transferInputValue}
                                    selectedAccount={selectedAccount}
                                    setSelectedAccount={setSelectedAccount}
                                    selectedProduct={selectedProduct}
                                />
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            {renderWarningMessage()}
        </>
    );
}